/**
 * Standby Hedged Strategy Confirmation Dialog
 *
 * Shows a confirmation dialog with live polled orderbooks for both Binance
 * Futures and the selected spot exchange. Allows configuration
 * of strategy parameters (budget, profit tiers) before execution.
 *
 * Uses the same ExchangeCard UI pattern from FuturesSymbolDetailPage.
 */

import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  Switch,
  FormControlLabel,
  Grid,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import { usePolledOrderbook } from "../../hooks/usePolledOrderbook";
import { usePriceUpdates } from "../../hooks/usePriceUpdates";
import { useMarkPrice } from "../../hooks/useMarkPrice";
import {
  SpotOrderbookCard,
  FuturesOrderbookCard,
  calculateBudgetCoverage,
} from "../TraderDashboard/OrderbookCards";
import { getUsdtKrwRate } from "../../utils/currencyConversion";
import {
  calculateRequiredSpendByExchange,
  calculateStrategyBudgetGuardrail,
  type BuyOpenSpendInput,
} from "../../utils/assetStatsBudget";
import { getApiConfigCached } from "../../config/api.config";
import {
  buildTraderApiBasePath,
  type TraderRouteVersion,
} from "../../utils/traderRouting";
import { updateSettings } from "../../features/settings/settingsSlice";
import type { RootState } from "../../app/store";

type ProfitTierPayload = {
  profit_percentage: number;
  volume_percentage_to_sell: number;
};

type HedgePayload = {
  leverage: number;
  rebalancing?: {
    price_surge_threshold: number;
    liquidation_proximity_threshold: number;
    spot_futures_gap_magnitude_ratio: number;
    entry_funding_rate_threshold: number | null;
    entry_funding_interval_hours?: number;
    low_funding_rate_threshold: number;
    funding_time_window_seconds: number;
  };
};

type BuyOpenModePayload =
  | { type: "Limit" }
  | { type: "Market"; max_notional_usdt: number };

type StandbyHedgedExecutionPayload = {
  config: {
    StandbyHedged: {
      opportunity: {
        buy_exchange: number;
        sell_exchange: number;
        base: string;
        buy_quote: "K" | "U" | "B";
        sell_quote: "K" | "U" | "B";
        quote_conversion_rate: number;
        max_profitable_volume: number;
      };
      hedge: HedgePayload;
      profit_tiers: ProfitTierPayload[];
      buyopen_mode: BuyOpenModePayload;
      replay_enabled: boolean;
      replay_delay_seconds: number;
    };
  };
};

// Signed percent fallback: must match the negative DEFAULT_SETTINGS convention.
// A positive value here would submit a positive ratio and arm immediate liquidation.
const DEFAULT_SPOT_FUTURES_GAP_MAGNITUDE_PERCENT = -3;

interface StandbyHedgedConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (payload: StandbyHedgedExecutionPayload) => Promise<void>;
  exchangeName: string;
  exchangeIndex: number;
  quoteCurrency: "K" | "U" | "B"; // KRW, USDT, or BTC
  baseAsset: string;
  traderName: string;
  traderRouteVersion: TraderRouteVersion;
  buyOpenSpendInputs: BuyOpenSpendInput[];
}

type WalletBalanceState =
  | { status: "idle" | "loading" | "error"; available: null }
  | { status: "ready"; available: number };

export const StandbyHedgedConfirmDialog: React.FC<
  StandbyHedgedConfirmDialogProps
> = ({
  open,
  onClose,
  onConfirm,
  exchangeName,
  exchangeIndex,
  quoteCurrency,
  baseAsset,
  traderName,
  traderRouteVersion,
  buyOpenSpendInputs,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { settings } = useSelector((state: RootState) => state.settings);

  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [walletBalance, setWalletBalance] = useState<WalletBalanceState>({
    status: "idle",
    available: null,
  });
  const [budgetInput, setBudgetInput] = useState(
    settings.strategyBudget.toString(),
  );
  const [profitInput, setProfitInput] = useState(
    (settings.profitTiers[0].profit_percentage * 100).toString(),
  );
  // Target difference ratio for BuyOpen entry condition (e.g., -0.5 means spot 0.5% cheaper than futures)
  const [targetDiffInput, setTargetDiffInput] = useState(
    settings.entryDiffPercent.toString(),
  );
  // Toggle between user-defined and current live diff
  const [useCurrentDiff, setUseCurrentDiff] = useState(false);

  // BuyOpen mode inputs
  const [buyOpenMode, setBuyOpenMode] = useState<"Limit" | "Market">(
    settings.buyOpenMode,
  );
  const [notionalInput, setNotionalInput] = useState(
    settings.marketNotionalUsdt.toString(),
  );

  // Hedge config inputs
  const [leverageInput, setLeverageInput] = useState(
    settings.leverage.toString(),
  );
  const [priceSurgeInput, setPriceSurgeInput] = useState(
    settings.hedgeConfig.priceSurgeThreshold.toString(),
  );
  const [liqProximityInput, setLiqProximityInput] = useState(
    settings.hedgeConfig.liquidationProximity.toString(),
  );
  const [spotFuturesGapInput, setSpotFuturesGapInput] = useState(
    (
      settings.hedgeConfig.spotFuturesGapMagnitude ??
      DEFAULT_SPOT_FUTURES_GAP_MAGNITUDE_PERCENT
    ).toString(),
  );
  const [entryFundingRateInput, setEntryFundingRateInput] = useState(
    settings.hedgeConfig.entryFundingRate === null
      ? ""
      : settings.hedgeConfig.entryFundingRate.toString(),
  );
  const [entryFundingIntervalInput, setEntryFundingIntervalInput] = useState(
    settings.hedgeConfig.entryFundingIntervalHours.toString(),
  );
  const [fundingRateInput, setFundingRateInput] = useState(
    settings.hedgeConfig.lowFundingRate.toString(),
  );
  const [fundingWindowInput, setFundingWindowInput] = useState(
    settings.hedgeConfig.fundingTimeWindow.toString(),
  );
  const [replayEnabled, setReplayEnabled] = useState(settings.replayEnabled);
  const [replayDelayInput, setReplayDelayInput] = useState(
    settings.replayDelaySeconds.toString(),
  );

  // Sync inputs when dialog opens
  useEffect(() => {
    if (open) {
      setBudgetInput(settings.strategyBudget.toString());
      setProfitInput(
        (settings.profitTiers[0].profit_percentage * 100).toString(),
      );
      setTargetDiffInput(settings.entryDiffPercent.toString());
      setUseCurrentDiff(false);
      setBuyOpenMode(settings.buyOpenMode);
      setNotionalInput(settings.marketNotionalUsdt.toString());
      setLeverageInput(settings.leverage.toString());
      setPriceSurgeInput(settings.hedgeConfig.priceSurgeThreshold.toString());
      setLiqProximityInput(
        settings.hedgeConfig.liquidationProximity.toString(),
      );
      setSpotFuturesGapInput(
        (
          settings.hedgeConfig.spotFuturesGapMagnitude ??
          DEFAULT_SPOT_FUTURES_GAP_MAGNITUDE_PERCENT
        ).toString(),
      );
      setEntryFundingRateInput(
        settings.hedgeConfig.entryFundingRate === null
          ? ""
          : settings.hedgeConfig.entryFundingRate.toString(),
      );
      setEntryFundingIntervalInput(
        settings.hedgeConfig.entryFundingIntervalHours.toString(),
      );
      setFundingRateInput(settings.hedgeConfig.lowFundingRate.toString());
      setFundingWindowInput(settings.hedgeConfig.fundingTimeWindow.toString());
      setReplayEnabled(settings.replayEnabled);
      setReplayDelayInput(settings.replayDelaySeconds.toString());
    }
  }, [
    open,
    settings.strategyBudget,
    settings.profitTiers,
    settings.entryDiffPercent,
    settings.buyOpenMode,
    settings.marketNotionalUsdt,
    settings.leverage,
    settings.hedgeConfig,
    settings.replayEnabled,
    settings.replayDelaySeconds,
  ]);

  // Map exchange index to exchange name for REST API
  const spotExchangeName = useMemo(() => {
    switch (exchangeIndex) {
      case 0:
        return "upbit";
      case 1:
        return "bithumb";
      case 3:
        return "binance";
      default:
        return "binance";
    }
  }, [exchangeIndex]);

  // Convert quote currency for hook
  const spotQuoteCurrency =
    quoteCurrency === "K" ? "KRW" : quoteCurrency === "B" ? "BTC" : "USDT";

  const walletExchangeName =
    spotExchangeName.charAt(0).toUpperCase() + spotExchangeName.slice(1);
  const guardrailQuoteAsset =
    quoteCurrency === "K" ? "KRW" : quoteCurrency === "U" ? "USDT" : null;

  useEffect(() => {
    if (!open || !guardrailQuoteAsset) {
      setWalletBalance({ status: "idle", available: null });
      return;
    }

    let cancelled = false;
    setWalletBalance({ status: "loading", available: null });

    const fetchWalletBalance = async () => {
      try {
        if (!traderName) {
          throw new Error("Missing trader name");
        }

        const apiConfig = getApiConfigCached();
        const apiBasePath = buildTraderApiBasePath(
          traderName,
          traderRouteVersion,
        );
        const apiBaseUrl = apiConfig.isProduction
          ? `${window.location.protocol}//${apiConfig.host}${apiBasePath}`
          : `http://${apiConfig.host}:${apiConfig.wsPort || 8765}${apiBasePath}`;
        const response = await fetch(`${apiBaseUrl}/balances`);

        if (!response.ok) {
          throw new Error(`Balance request failed with HTTP ${response.status}`);
        }

        const data = (await response.json()) as {
          balances?: Array<{
            exchange: string;
            asset: string;
            balance: number;
          }>;
        };
        const balance = data.balances?.find(
          (candidate) =>
            candidate.exchange.toLowerCase() ===
              walletExchangeName.toLowerCase() &&
            candidate.asset.toUpperCase() === guardrailQuoteAsset,
        )?.balance;

        if (!Number.isFinite(balance) || (balance ?? -1) < 0) {
          throw new Error("Available wallet balance was not returned");
        }

        if (!cancelled) {
          setWalletBalance({ status: "ready", available: balance as number });
        }
      } catch {
        if (!cancelled) {
          setWalletBalance({ status: "error", available: null });
        }
      }
    };

    void fetchWalletBalance();

    return () => {
      cancelled = true;
    };
  }, [
    guardrailQuoteAsset,
    open,
    traderName,
    traderRouteVersion,
    walletExchangeName,
  ]);

  // CCXT uses unified symbol format: "BTC/USDT", "BTC/KRW"
  const futuresCcxtSymbol = `${baseAsset}/USDT`;
  const spotCcxtSymbol = `${baseAsset}/${spotQuoteCurrency}`;

  // Poll Binance Futures via CCXT first, then use the normalized backend book
  // for this dialog session when the canonical market is unavailable in CCXT.
  const futuresState = usePolledOrderbook({
    exchange: "binanceusdm",
    symbol: futuresCcxtSymbol,
    enabled: open,
    isFutures: true,
    backendFuturesFallback: {
      exchange: "binance",
      canonicalSymbol: `${baseAsset}USDT`,
      requiredSide: "bids",
    },
  });

  // Get mark price data for funding rate and timer
  const markPriceData = useMarkPrice(futuresCcxtSymbol);

  // Poll spot exchange orderbook via CCXT REST (1 second interval until ready)
  const spotState = usePolledOrderbook({
    exchange: spotExchangeName,
    symbol: spotCcxtSymbol,
    enabled: open,
    isFutures: false,
  });

  // Get price data for conversion rates
  const priceData = usePriceUpdates();

  // KRW/USDT rate - use BTC cross-rate (upbitBtcKrw / binanceBtcUsdt)
  const krwToUsdtRate = useMemo(() => {
    return getUsdtKrwRate(priceData);
  }, [priceData]);

  // BTC/USDT rate (for converting USDT budget to BTC)
  const btcUsdtRate = priceData?.binanceBtcUsdt ?? null;

  const reservedSpendQuote = useMemo(() => {
    const requiredSpendByExchange = calculateRequiredSpendByExchange(
      buyOpenSpendInputs,
      krwToUsdtRate,
    );
    return requiredSpendByExchange[walletExchangeName]?.amount ?? 0;
  }, [buyOpenSpendInputs, krwToUsdtRate, walletExchangeName]);

  const requestedBudgetUsdt = parseFloat(budgetInput);
  const budgetGuardrail = useMemo(() => {
    if (
      !guardrailQuoteAsset ||
      walletBalance.status !== "ready" ||
      !Number.isFinite(requestedBudgetUsdt) ||
      requestedBudgetUsdt < 0
    ) {
      return null;
    }

    return calculateStrategyBudgetGuardrail({
      availableBalanceQuote: walletBalance.available,
      reservedSpendQuote,
      requestedBudgetUsdt,
      quoteAsset: guardrailQuoteAsset,
      usdtKrwRate: krwToUsdtRate,
    });
  }, [
    guardrailQuoteAsset,
    krwToUsdtRate,
    requestedBudgetUsdt,
    reservedSpendQuote,
    walletBalance,
  ]);

  const budgetGuardrailPending =
    Boolean(guardrailQuoteAsset) &&
    (walletBalance.status === "idle" || walletBalance.status === "loading");
  const budgetGuardrailUnavailable =
    Boolean(guardrailQuoteAsset) &&
    (walletBalance.status === "error" ||
      (guardrailQuoteAsset === "KRW" &&
        (!krwToUsdtRate || krwToUsdtRate <= 0)));
  const budgetGuardrailBlocksExecution =
    budgetGuardrailPending ||
    budgetGuardrailUnavailable ||
    Boolean(budgetGuardrail?.blocked);

  const formatGuardrailAmount = (amount: number) =>
    new Intl.NumberFormat(
      guardrailQuoteAsset === "KRW" ? "ko-KR" : "en-US",
      {
        maximumFractionDigits: guardrailQuoteAsset === "KRW" ? 0 : 2,
      },
    ).format(amount);

  // Calculate budget in quote currency for spot
  const spotBudgetInQuote = useMemo(() => {
    const budget = settings.strategyBudget;
    if (budget < 10) return 0; // Minimum budget is 10 USDT

    if (quoteCurrency === "K" && krwToUsdtRate) {
      return budget * krwToUsdtRate; // Convert USDT to KRW
    }
    if (quoteCurrency === "B" && btcUsdtRate) {
      return budget / btcUsdtRate; // Convert USDT to BTC
    }
    return budget; // Already in USDT
  }, [settings.strategyBudget, quoteCurrency, krwToUsdtRate, btcUsdtRate]);

  // Prepare sorted orderbook data (from polled REST data)
  const sortedFuturesBids = useMemo(() => {
    return [...(futuresState.bids || [])].sort((a, b) => b.price - a.price);
  }, [futuresState.bids]);

  const sortedSpotAsks = useMemo(() => {
    return [...(spotState.asks || [])].sort((a, b) => a.price - b.price);
  }, [spotState.asks]);

  const futuresMarketUnavailable =
    Boolean(futuresState.error) ||
    (!futuresState.loading && sortedFuturesBids.length === 0);
  const spotMarketUnavailable =
    Boolean(spotState.error) ||
    (!spotState.loading && sortedSpotAsks.length === 0);

  // Calculate budget coverage for futures (USDT)
  const futuresCoverage = useMemo(() => {
    return calculateBudgetCoverage(sortedFuturesBids, settings.strategyBudget);
  }, [sortedFuturesBids, settings.strategyBudget]);

  // Calculate budget coverage for spot (in quote currency)
  const spotCoverage = useMemo(() => {
    return calculateBudgetCoverage(sortedSpotAsks, spotBudgetInQuote);
  }, [sortedSpotAsks, spotBudgetInQuote]);

  // Calculate differential percentage based on AVERAGE prices (not best bid/ask)
  // Formula: (spot_ask - futures_bid) / spot_ask
  const diffPercent = useMemo(() => {
    const futuresAvgBid = futuresCoverage.avgPrice;
    const spotAvgAsk = spotCoverage.avgPrice;

    if (!futuresAvgBid || !spotAvgAsk) return null;

    // Convert spot avg to USDT if needed
    let spotAvgInUsdt = spotAvgAsk;
    if (quoteCurrency === "K" && krwToUsdtRate) {
      // KRW to USDT: divide by rate
      spotAvgInUsdt = spotAvgAsk / krwToUsdtRate;
    } else if (quoteCurrency === "B" && btcUsdtRate) {
      // BTC to USDT: multiply by BTC/USDT rate
      // spotAvgAsk is in BTC (e.g., 0.03 BTC for 1 ETH)
      // btcUsdtRate is BTC/USDT (e.g., 100000 USDT per BTC)
      // So: 0.03 BTC * 100000 = 3000 USDT
      spotAvgInUsdt = spotAvgAsk * btcUsdtRate;
    }

    if (spotAvgInUsdt <= 0) return null;
    return ((spotAvgInUsdt - futuresAvgBid) / spotAvgInUsdt) * 100;
  }, [
    futuresCoverage.avgPrice,
    spotCoverage.avgPrice,
    quoteCurrency,
    krwToUsdtRate,
    btcUsdtRate,
  ]);

  // Handle budget change
  const handleBudgetChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setBudgetInput(value);

    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue > 0) {
      dispatch(
        updateSettings({
          ...settings,
          strategyBudget: numValue,
        }),
      );
    }
  };

  // Handle profit change (single tier mode only - only updates profit %, not volume)
  const handleProfitChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setProfitInput(value);

    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      // Only update profit_percentage for tier 0, keep volume unchanged
      // (volume is ignored for single tier mode anyway - always 100%)
      const newTiers = settings.profitTiers.map((tier, index) =>
        index === 0
          ? { ...tier, profit_percentage: numValue / 100 }
          : { ...tier },
      );
      dispatch(
        updateSettings({
          ...settings,
          profitTiers: newTiers,
        }),
      );
    }
  };

  // Calculate total volume for multi-tier validation
  const totalMultiTierVolume = useMemo(() => {
    return settings.profitTiers.reduce(
      (sum, tier) => sum + tier.volume_percentage_to_sell,
      0,
    );
  }, [settings.profitTiers]);

  const isMultiTierVolumeValid = Math.abs(totalMultiTierVolume - 1.0) < 0.001;

  // Build JSON payload for execution
  // New API: No price levels needed, BuyOpen monitors live orderbooks
  // Required fields: max_profitable_volume (budget), quote_conversion_rate (target entry difference)
  const jsonPayload = useMemo<StandbyHedgedExecutionPayload | null>(() => {
    // Validate inputs
    const budget = parseFloat(budgetInput);

    // Use either user-defined or current live diff based on toggle
    const effectiveDiff = useCurrentDiff
      ? diffPercent
      : parseFloat(targetDiffInput);

    if (!baseAsset || isNaN(budget) || budget < 10) {
      return null;
    }

    if (effectiveDiff === null || isNaN(effectiveDiff)) {
      return null;
    }

    // Validate market notional: must be between $5 and budget
    if (buyOpenMode === "Market") {
      const notional = parseFloat(notionalInput);
      if (isNaN(notional) || notional < 5 || notional > budget) {
        return null;
      }
    }

    const profit_tiers = settings.useMultiTierProfit
      ? settings.profitTiers
          .filter((tier) => tier.volume_percentage_to_sell > 0)
          .map((tier) => ({
            profit_percentage: tier.profit_percentage,
            volume_percentage_to_sell: tier.volume_percentage_to_sell,
          }))
      : [
          {
            profit_percentage: settings.profitTiers[0].profit_percentage,
            volume_percentage_to_sell: 1.0,
          },
        ];

    // Parse hedge config inputs
    const leverage = parseFloat(leverageInput);
    const priceSurge = parseFloat(priceSurgeInput) / 100; // Convert % to decimal
    const liqProximity = parseFloat(liqProximityInput) / 100; // Convert % to decimal
    const spotFuturesGap = parseFloat(spotFuturesGapInput) / 100; // Convert % to decimal
    const entryFundingRate =
      entryFundingRateInput.trim() === ""
        ? null
        : parseFloat(entryFundingRateInput) / 100;
    const entryFundingIntervalHours =
      entryFundingRate === null ? 8 : parseInt(entryFundingIntervalInput, 10);
    const fundingRate = parseFloat(fundingRateInput) / 100; // Convert % to decimal
    const fundingWindow = parseFloat(fundingWindowInput);
    const replayDelaySeconds = parseFloat(replayDelayInput);

    if (!Number.isFinite(spotFuturesGap)) {
      return null;
    }

    if (
      entryFundingRateInput.trim() !== "" &&
      (entryFundingRate === null || Number.isNaN(entryFundingRate))
    ) {
      return null;
    }

    if (
      entryFundingRate !== null &&
      ![0, 1, 4, 8].includes(entryFundingIntervalHours)
    ) {
      return null;
    }

    // Check if hedge config differs from defaults
    const isDefaultHedgeConfig =
      leverage === 2 &&
      Math.abs(priceSurge - 0.5) < 0.001 &&
      Math.abs(liqProximity - 0.9) < 0.001 &&
      Math.abs(spotFuturesGap - -0.03) < 0.0001 &&
      entryFundingRate === null &&
      Math.abs(fundingRate - -0.005) < 0.0001 &&
      fundingWindow === 600;

    // Build hedge config (only include if non-default)
    const hedge: HedgePayload = { leverage };
    if (!isDefaultHedgeConfig) {
      hedge.rebalancing = {
        price_surge_threshold: priceSurge,
        liquidation_proximity_threshold: liqProximity,
        spot_futures_gap_magnitude_ratio: spotFuturesGap,
        entry_funding_rate_threshold: entryFundingRate,
        entry_funding_interval_hours:
          entryFundingRate === null ? undefined : entryFundingIntervalHours,
        low_funding_rate_threshold: fundingRate,
        funding_time_window_seconds: fundingWindow,
      };
    }

    if (
      replayEnabled &&
      (isNaN(replayDelaySeconds) || replayDelaySeconds < 0)
    ) {
      return null;
    }

    // Build buyopen_mode (Limit is default, only include Market with notional)
    const buyopen_mode: BuyOpenModePayload =
      buyOpenMode === "Market"
        ? {
            type: "Market" as const,
            max_notional_usdt: parseFloat(notionalInput) || 5,
          }
        : { type: "Limit" as const };

    return {
      config: {
        StandbyHedged: {
          opportunity: {
            buy_exchange: exchangeIndex,
            sell_exchange: exchangeIndex,
            base: baseAsset,
            buy_quote: quoteCurrency,
            sell_quote: quoteCurrency,
            quote_conversion_rate: effectiveDiff / 100,
            max_profitable_volume: budget,
          },
          hedge,
          profit_tiers,
          buyopen_mode,
          replay_enabled: replayEnabled,
          replay_delay_seconds: isNaN(replayDelaySeconds)
            ? 0
            : replayDelaySeconds,
        },
      },
    };
  }, [
    exchangeIndex,
    quoteCurrency,
    baseAsset,
    budgetInput,
    targetDiffInput,
    useCurrentDiff,
    diffPercent,
    settings,
    leverageInput,
    priceSurgeInput,
    liqProximityInput,
    spotFuturesGapInput,
    entryFundingRateInput,
    entryFundingIntervalInput,
    fundingRateInput,
    fundingWindowInput,
    buyOpenMode,
    notionalInput,
    replayEnabled,
    replayDelayInput,
  ]);

  const handleConfirm = async () => {
    if (budgetGuardrailBlocksExecution) {
      setError(
        t(
          budgetGuardrail?.blocked
            ? "traderDashboard.standbyHedged.budgetGuardrail.exceeded"
            : "traderDashboard.standbyHedged.budgetGuardrail.unavailable",
        ),
      );
      return;
    }

    if (futuresMarketUnavailable) {
      setError(
        t("traderDashboard.standbyHedged.errorFuturesMarketUnavailable", {
          symbol: futuresCcxtSymbol,
          defaultValue: `Binance Futures market data is not available for ${futuresCcxtSymbol}.`,
        }),
      );
      return;
    }

    if (spotMarketUnavailable) {
      setError(
        t("traderDashboard.standbyHedged.errorSpotMarketUnavailable", {
          symbol: spotCcxtSymbol,
          defaultValue: `Spot market data is not available for ${spotCcxtSymbol}.`,
        }),
      );
      return;
    }

    if (!jsonPayload) {
      setError(t("traderDashboard.standbyHedged.errorNotReady"));
      return;
    }

    setExecuting(true);
    setError(null);

    try {
      // Save the effective diff value used for this execution as the new default
      const effectiveDiff = useCurrentDiff
        ? diffPercent
        : parseFloat(targetDiffInput);

      // Save hedge config settings
      const leverage = parseFloat(leverageInput);
      const priceSurge = parseFloat(priceSurgeInput);
      const liqProximity = parseFloat(liqProximityInput);
      const spotFuturesGap = parseFloat(spotFuturesGapInput);
      const entryFundingRate =
        entryFundingRateInput.trim() === ""
          ? null
          : parseFloat(entryFundingRateInput);
      const entryFundingIntervalHours =
        entryFundingRate === null
          ? 8
          : (parseInt(entryFundingIntervalInput, 10) as 0 | 1 | 4 | 8);
      const fundingRate = parseFloat(fundingRateInput);
      const fundingWindow = parseFloat(fundingWindowInput);
      const replayDelaySeconds = parseFloat(replayDelayInput);

      if (
        effectiveDiff !== null &&
        !isNaN(effectiveDiff) &&
        !isNaN(leverage) &&
        !isNaN(priceSurge) &&
        !isNaN(liqProximity) &&
        !isNaN(spotFuturesGap) &&
        !isNaN(fundingRate) &&
        !isNaN(fundingWindow) &&
        !isNaN(replayDelaySeconds)
      ) {
        dispatch(
          updateSettings({
            ...settings,
            entryDiffPercent: effectiveDiff,
            leverage,
            hedgeConfig: {
              priceSurgeThreshold: priceSurge,
              liquidationProximity: liqProximity,
              spotFuturesGapMagnitude: spotFuturesGap,
              entryFundingRate,
              entryFundingIntervalHours,
              lowFundingRate: fundingRate,
              fundingTimeWindow: fundingWindow,
            },
            buyOpenMode,
            marketNotionalUsdt: parseFloat(notionalInput) || 5,
            replayEnabled,
            replayDelaySeconds,
          }),
        );
      }

      await onConfirm(jsonPayload);
      // Parent handles closing and tab switching on success
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to execute strategy",
      );
      setExecuting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={executing ? undefined : onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          background:
            "linear-gradient(135deg, rgba(15,18,28,0.98) 0%, rgba(9,12,20,1) 100%)",
          border: "1px solid rgba(255,255,255,0.1)",
        },
      }}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={2}>
          <CheckCircleIcon color="primary" fontSize="large" />
          <Box>
            <Typography variant="h5" fontWeight="bold">
              {t("traderDashboard.standbyHedged.title", { symbol: baseAsset })}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("traderDashboard.standbyHedged.subtitle", {
                exchange: exchangeName,
                quote:
                  quoteCurrency === "K"
                    ? "KRW"
                    : quoteCurrency === "B"
                      ? "BTC"
                      : "USDT",
              })}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          {/* Error Alert */}
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Two-Column Configuration Layout */}
          <Grid container spacing={3}>
            {/* Left Column: Strategy Configuration */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  background: "rgba(25,118,210,0.1)",
                  border: "1px solid rgba(25,118,210,0.3)",
                  borderRadius: 2,
                  height: "100%",
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 2 }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    {t("traderDashboard.standbyHedged.config.title")}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="caption" color="text.secondary">
                      {t("traderDashboard.standbyHedged.config.single")}
                    </Typography>
                    <Switch
                      checked={settings.useMultiTierProfit}
                      onChange={(e) => {
                        dispatch(
                          updateSettings({
                            ...settings,
                            useMultiTierProfit: e.target.checked,
                          }),
                        );
                      }}
                      size="small"
                    />
                    <Typography variant="caption" color="text.secondary">
                      {t("traderDashboard.standbyHedged.config.multi")}
                    </Typography>
                  </Stack>
                </Stack>

                {/* Budget Input */}
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{ mb: 2 }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="bold"
                    sx={{ minWidth: 100 }}
                  >
                    {t("traderDashboard.standbyHedged.config.budget")}
                  </Typography>
                  <TextField
                    label="USDT"
                    type="number"
                    value={budgetInput}
                    onChange={handleBudgetChange}
                    size="small"
                    inputProps={{ min: 10, step: 1 }}
                    error={
                      parseFloat(budgetInput) < 10 ||
                      Boolean(budgetGuardrail?.blocked)
                    }
                    helperText={
                      parseFloat(budgetInput) < 10 ? "Min: 10 USDT" : ""
                    }
                    sx={{ width: 150 }}
                  />
                  {krwToUsdtRate && (
                    <Typography variant="caption" color="text.secondary">
                      (≈ ₩
                      {(parseFloat(budgetInput) * krwToUsdtRate).toLocaleString(
                        "ko-KR",
                        { maximumFractionDigits: 0 },
                      )}
                      )
                    </Typography>
                  )}
                </Stack>

                {budgetGuardrailPending ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {t(
                      "traderDashboard.standbyHedged.budgetGuardrail.checking",
                    )}
                  </Alert>
                ) : budgetGuardrailUnavailable ? (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {t(
                      "traderDashboard.standbyHedged.budgetGuardrail.unavailable",
                      {
                        exchange: walletExchangeName,
                        currency: guardrailQuoteAsset,
                      },
                    )}
                  </Alert>
                ) : budgetGuardrail?.blocked ? (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {t(
                      "traderDashboard.standbyHedged.budgetGuardrail.exceeded",
                      {
                        exchange: walletExchangeName,
                        currency: budgetGuardrail.quoteAsset,
                        available: formatGuardrailAmount(
                          budgetGuardrail.availableBalanceQuote,
                        ),
                        reserved: formatGuardrailAmount(
                          budgetGuardrail.reservedSpendQuote,
                        ),
                        requested: formatGuardrailAmount(
                          budgetGuardrail.requestedSpendQuote,
                        ),
                        shortfall: formatGuardrailAmount(
                          budgetGuardrail.shortfallQuote,
                        ),
                      },
                    )}
                  </Alert>
                ) : null}

                {/* Target Entry Difference Input */}
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{ mb: 2 }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="bold"
                    sx={{ minWidth: 100 }}
                  >
                    {t("traderDashboard.standbyHedged.config.entryDiff")}
                  </Typography>
                  <TextField
                    label="%"
                    type="text"
                    value={
                      useCurrentDiff && diffPercent !== null
                        ? diffPercent.toFixed(2)
                        : targetDiffInput
                    }
                    onChange={(e) => {
                      setTargetDiffInput(e.target.value);
                      setUseCurrentDiff(false);
                    }}
                    onBlur={() => {
                      // Save to settings when focus leaves (if valid number)
                      const numValue = parseFloat(targetDiffInput);
                      if (!isNaN(numValue)) {
                        dispatch(
                          updateSettings({
                            ...settings,
                            entryDiffPercent: numValue,
                          }),
                        );
                      }
                    }}
                    size="small"
                    inputProps={{ inputMode: "decimal" }}
                    sx={{ width: 120 }}
                  />
                  <Chip
                    label={t(
                      "traderDashboard.standbyHedged.config.currentDiff",
                    )}
                    size="small"
                    variant="filled"
                    onClick={() => setUseCurrentDiff(!useCurrentDiff)}
                    disabled={diffPercent === null}
                    sx={{
                      cursor: diffPercent !== null ? "pointer" : "default",
                      fontWeight: 600,
                      ...(useCurrentDiff
                        ? { bgcolor: "success.main", color: "#000" }
                        : {
                            bgcolor: "rgba(255,255,255,0.08)",
                            color: "text.disabled",
                          }),
                    }}
                  />
                </Stack>

                {/* Spot/Futures Gap Threshold */}
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{ mb: 2 }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="bold"
                    sx={{ minWidth: 100 }}
                  >
                    {t(
                      "traderDashboard.updateHedgeConfig.spotFuturesGapMagnitude",
                    )}
                  </Typography>
                  <TextField
                    label="%"
                    type="text"
                    value={spotFuturesGapInput}
                    onChange={(e) => setSpotFuturesGapInput(e.target.value)}
                    size="small"
                    inputProps={{ inputMode: "decimal" }}
                    error={!Number.isFinite(parseFloat(spotFuturesGapInput))}
                    sx={{ width: 120 }}
                  />
                </Stack>

                {/* BuyOpen Mode */}
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{ mb: 2 }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="bold"
                    sx={{ minWidth: 100 }}
                  >
                    {t("traderDashboard.standbyHedged.config.buyOpenMode")}
                  </Typography>
                  <Chip
                    label={t("traderDashboard.standbyHedged.config.limitOrder")}
                    size="small"
                    variant="filled"
                    onClick={() => setBuyOpenMode("Limit")}
                    sx={{
                      cursor: "pointer",
                      fontWeight: 600,
                      ...(buyOpenMode === "Limit"
                        ? { bgcolor: "primary.main", color: "#000" }
                        : {
                            bgcolor: "rgba(255,255,255,0.08)",
                            color: "text.disabled",
                          }),
                    }}
                  />
                  <Chip
                    label={t(
                      "traderDashboard.standbyHedged.config.marketOrder",
                    )}
                    size="small"
                    variant="filled"
                    onClick={() => setBuyOpenMode("Market")}
                    sx={{
                      cursor: "pointer",
                      fontWeight: 600,
                      ...(buyOpenMode === "Market"
                        ? { bgcolor: "warning.main", color: "#000" }
                        : {
                            bgcolor: "rgba(255,255,255,0.08)",
                            color: "text.disabled",
                          }),
                    }}
                  />
                  {buyOpenMode === "Market" &&
                    (() => {
                      const notionalVal = parseFloat(notionalInput);
                      const budgetVal = parseFloat(budgetInput);
                      const isError =
                        notionalVal < 5 || notionalVal > budgetVal;
                      const helperText =
                        notionalVal < 5
                          ? "Min: $5"
                          : notionalVal > budgetVal
                            ? `Max: $${budgetVal}`
                            : "";
                      return (
                        <TextField
                          label={t(
                            "traderDashboard.standbyHedged.config.maxNotional",
                          )}
                          type="number"
                          value={notionalInput}
                          onChange={(e) => setNotionalInput(e.target.value)}
                          size="small"
                          inputProps={{ min: 5, max: budgetVal, step: 1 }}
                          error={isError}
                          helperText={helperText}
                          sx={{ width: 160 }}
                        />
                      );
                    })()}
                </Stack>

                {/* Profit Tiers */}
                <Stack spacing={2}>
                  {/* Single Tier Mode */}
                  {!settings.useMultiTierProfit && (
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight="bold"
                        sx={{ minWidth: 100 }}
                      >
                        {t("traderDashboard.standbyHedged.config.target")}
                      </Typography>
                      <TextField
                        label={t(
                          "traderDashboard.standbyHedged.config.profitPercent",
                        )}
                        type="number"
                        value={profitInput}
                        onChange={handleProfitChange}
                        size="small"
                        inputProps={{ min: 0, step: 0.1 }}
                        sx={{ width: 150 }}
                      />
                      <TextField
                        label={t(
                          "traderDashboard.standbyHedged.config.sellVolumePercent",
                        )}
                        type="number"
                        value="100"
                        disabled
                        size="small"
                        sx={{ width: 150 }}
                      />
                    </Stack>
                  )}

                  {/* Multi Tier Mode */}
                  {settings.useMultiTierProfit && (
                    <>
                      {/* Header with Add button */}
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight="bold"
                        >
                          {t(
                            "traderDashboard.standbyHedged.config.profitTiers",
                          )}
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => {
                            const newTiers = [
                              ...settings.profitTiers,
                              {
                                profit_percentage: 0.15,
                                volume_percentage_to_sell: 0,
                              },
                            ];
                            dispatch(
                              updateSettings({
                                ...settings,
                                profitTiers: newTiers,
                              }),
                            );
                          }}
                        >
                          {t("traderDashboard.standbyHedged.config.addTier")}
                        </Button>
                      </Stack>

                      {settings.profitTiers.map((tier, index) => (
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          key={index}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight="bold"
                            sx={{ minWidth: 100 }}
                          >
                            {t("traderDashboard.standbyHedged.config.tier", {
                              num: index + 1,
                            })}
                          </Typography>
                          <TextField
                            label={t(
                              "traderDashboard.standbyHedged.config.profitPercent",
                            )}
                            type="number"
                            value={parseFloat(
                              (tier.profit_percentage * 100).toFixed(4),
                            )}
                            onChange={(e) => {
                              const newTiers = settings.profitTiers.map(
                                (t, i) =>
                                  i === index
                                    ? {
                                        ...t,
                                        profit_percentage:
                                          parseFloat(e.target.value) / 100,
                                      }
                                    : { ...t },
                              );
                              dispatch(
                                updateSettings({
                                  ...settings,
                                  profitTiers: newTiers,
                                }),
                              );
                            }}
                            size="small"
                            inputProps={{ min: 0, step: 0.1 }}
                            sx={{ width: 120 }}
                          />
                          <TextField
                            label={t(
                              "traderDashboard.standbyHedged.config.sellVolumePercent",
                            )}
                            type="number"
                            value={(
                              tier.volume_percentage_to_sell * 100
                            ).toFixed(0)}
                            onChange={(e) => {
                              const newTiers = settings.profitTiers.map(
                                (t, i) =>
                                  i === index
                                    ? {
                                        ...t,
                                        volume_percentage_to_sell:
                                          parseFloat(e.target.value) / 100,
                                      }
                                    : { ...t },
                              );
                              dispatch(
                                updateSettings({
                                  ...settings,
                                  profitTiers: newTiers,
                                }),
                              );
                            }}
                            size="small"
                            inputProps={{ min: 0, max: 100, step: 1 }}
                            sx={{ width: 120 }}
                            helperText={
                              tier.volume_percentage_to_sell === 0
                                ? t(
                                    "traderDashboard.standbyHedged.config.remaining",
                                  )
                                : ""
                            }
                          />
                          <IconButton
                            size="small"
                            onClick={() => {
                              // Minimum 2 tiers for multi-tier mode (otherwise same as single)
                              if (settings.profitTiers.length > 2) {
                                const newTiers = settings.profitTiers.filter(
                                  (_, i) => i !== index,
                                );
                                dispatch(
                                  updateSettings({
                                    ...settings,
                                    profitTiers: newTiers,
                                  }),
                                );
                              }
                            }}
                            disabled={settings.profitTiers.length <= 2}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Stack>
                      ))}
                      {/* Volume sum validation */}
                      <Typography
                        variant="caption"
                        sx={{
                          color: isMultiTierVolumeValid
                            ? "success.main"
                            : "error.main",
                          fontWeight: 600,
                        }}
                      >
                        {t(
                          "traderDashboard.standbyHedged.config.totalSellVolume",
                          { percent: (totalMultiTierVolume * 100).toFixed(0) },
                        )}
                        {!isMultiTierVolumeValid &&
                          ` ${t("traderDashboard.standbyHedged.config.mustEqual100")}`}
                      </Typography>
                    </>
                  )}
                </Stack>

                {/* Replay controls */}
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{ mt: 2 }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="bold"
                    sx={{ minWidth: 100 }}
                  >
                    {t("traderDashboard.standbyHedged.replay", "Replay")}
                  </Typography>
                  <Switch
                    checked={replayEnabled}
                    onChange={(e) => setReplayEnabled(e.target.checked)}
                    color="primary"
                  />
                  <TextField
                    label={t(
                      "traderDashboard.standbyHedged.replayDelay",
                      "Delay (sec)",
                    )}
                    type="number"
                    value={replayDelayInput}
                    onChange={(e) => setReplayDelayInput(e.target.value)}
                    size="small"
                    inputProps={{ min: 0, step: 30 }}
                    disabled={!replayEnabled}
                    sx={{ width: 140 }}
                  />
                </Stack>
              </Paper>
            </Grid>

            {/* Right Column: Hedge Configuration */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  background: "rgba(255,152,0,0.1)",
                  border: "1px solid rgba(255,152,0,0.3)",
                  borderRadius: 2,
                  height: "100%",
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ mb: 2 }}
                >
                  {t(
                    "traderDashboard.standbyHedged.hedgeConfig.title",
                    "Hedge Configuration",
                  )}
                </Typography>

                {/* Leverage */}
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{ mb: 2 }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="bold"
                    sx={{ minWidth: 150 }}
                  >
                    {t("traderDashboard.updateHedgeConfig.leverage")}
                  </Typography>
                  <TextField
                    label="x"
                    type="number"
                    value={leverageInput}
                    onChange={(e) => setLeverageInput(e.target.value)}
                    size="small"
                    inputProps={{ min: 1, max: 20, step: 1 }}
                    sx={{ width: 120 }}
                  />
                </Stack>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight="bold"
                  sx={{ display: "block", mb: 1 }}
                >
                  {t("traderDashboard.updateHedgeConfig.rebalancingConfig")}
                </Typography>

                {/* Price Surge Threshold */}
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{ mb: 1.5 }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="bold"
                    sx={{ minWidth: 150 }}
                  >
                    {t("traderDashboard.updateHedgeConfig.priceSurgeThreshold")}
                  </Typography>
                  <TextField
                    label="%"
                    type="number"
                    value={priceSurgeInput}
                    onChange={(e) => setPriceSurgeInput(e.target.value)}
                    size="small"
                    inputProps={{ min: 0, max: 100, step: 5 }}
                    sx={{ width: 120 }}
                  />
                </Stack>

                {/* Liquidation Proximity */}
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{ mb: 1.5 }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="bold"
                    sx={{ minWidth: 150 }}
                  >
                    {t(
                      "traderDashboard.updateHedgeConfig.liquidationProximity",
                    )}
                  </Typography>
                  <TextField
                    label="%"
                    type="number"
                    value={liqProximityInput}
                    onChange={(e) => setLiqProximityInput(e.target.value)}
                    size="small"
                    inputProps={{ min: 50, max: 99, step: 5 }}
                    sx={{ width: 120 }}
                  />
                </Stack>

                {/* Entry Funding Threshold */}
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 1.5 }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="bold"
                    sx={{ minWidth: 150 }}
                  >
                    {t(
                      "traderDashboard.standbyHedged.entryFundingRate",
                      "Entry Funding Rate",
                    )}
                  </Typography>
                  <TextField
                    label="%"
                    type="number"
                    value={entryFundingRateInput}
                    onChange={(e) => setEntryFundingRateInput(e.target.value)}
                    size="small"
                    inputProps={{ min: -5, max: 5, step: 0.001 }}
                    placeholder={t(
                      "traderDashboard.standbyHedged.optional",
                      "optional",
                    )}
                    sx={{ width: 120 }}
                  />
                  <TextField
                    select
                    label={t(
                      "traderDashboard.updateHedgeConfig.entryFundingInterval",
                    )}
                    value={entryFundingIntervalInput}
                    onChange={(e) =>
                      setEntryFundingIntervalInput(e.target.value)
                    }
                    size="small"
                    disabled={entryFundingRateInput.trim() === ""}
                    SelectProps={{ native: true }}
                    sx={{ width: 120 }}
                  >
                    <option value="0">
                      {t(
                        "traderDashboard.updateHedgeConfig.entryFundingIntervalDontCare",
                      )}
                    </option>
                    <option value="1">
                      {t(
                        "traderDashboard.updateHedgeConfig.entryFundingIntervalHoursValue",
                        { count: 1 },
                      )}
                    </option>
                    <option value="4">
                      {t(
                        "traderDashboard.updateHedgeConfig.entryFundingIntervalHoursValue",
                        { count: 4 },
                      )}
                    </option>
                    <option value="8">
                      {t(
                        "traderDashboard.updateHedgeConfig.entryFundingIntervalHoursValue",
                        { count: 8 },
                      )}
                    </option>
                  </TextField>
                </Stack>

                {/* Low Funding Rate and Time Window - Combined Row */}
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="bold"
                    sx={{ minWidth: 150 }}
                  >
                    {t("traderDashboard.updateHedgeConfig.lowFundingRate")}
                  </Typography>
                  <TextField
                    label="%"
                    type="number"
                    value={fundingRateInput}
                    onChange={(e) => setFundingRateInput(e.target.value)}
                    size="small"
                    inputProps={{ min: -5, max: 0, step: 0.1 }}
                    sx={{ width: 80 }}
                  />
                  <TextField
                    label="초"
                    type="number"
                    value={fundingWindowInput}
                    onChange={(e) => setFundingWindowInput(e.target.value)}
                    size="small"
                    inputProps={{ min: 60, max: 3600, step: 60 }}
                    sx={{ width: 80 }}
                  />
                </Stack>
              </Paper>
            </Grid>
          </Grid>

          {/* Conversion Rate Info */}
          {quoteCurrency === "K" && (
            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                background: "rgba(255,152,0,0.1)",
                border: "1px solid rgba(255,152,0,0.3)",
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" color="warning.main">
                {t("traderDashboard.standbyHedged.usdtKrwRate", {
                  rate: krwToUsdtRate
                    ? krwToUsdtRate.toFixed(2)
                    : t("traderDashboard.standbyHedged.loadingRate"),
                })}
              </Typography>
            </Paper>
          )}

          {/* Orderbooks Grid - Futures on left (with funding), Spot on right (with diff %) */}
          <Grid container spacing={3}>
            {/* Futures Orderbook (Left) - Funding Rate + Timer */}
            <Grid size={{ xs: 12, md: 6 }}>
              <FuturesOrderbookCard
                bids={sortedFuturesBids}
                loading={futuresState.loading && sortedFuturesBids.length === 0}
                error={futuresState.error}
                fundingRate={markPriceData?.fundingRate ?? null}
                nextFundingTime={markPriceData?.nextFundingTime ?? null}
                avgPrice={futuresCoverage.avgPrice}
                highlightCount={futuresCoverage.highlightCount}
              />
            </Grid>

            {/* Spot Orderbook (Right) - Differential % */}
            <Grid size={{ xs: 12, md: 6 }}>
              <SpotOrderbookCard
                exchangeName={exchangeName}
                exchangeIndex={exchangeIndex}
                asks={sortedSpotAsks}
                loading={spotState.loading && sortedSpotAsks.length === 0}
                error={spotState.error}
                quoteCurrency={quoteCurrency}
                diffPercent={diffPercent}
                avgPrice={spotCoverage.avgPrice}
                highlightCount={spotCoverage.highlightCount}
                quoteToUsdtRate={
                  quoteCurrency === "K"
                    ? krwToUsdtRate
                    : quoteCurrency === "B"
                      ? btcUsdtRate
                      : null
                }
              />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2,
          gap: 1,
          flexWrap: "wrap",
          justifyContent: "flex-start",
        }}
      >
        <FormControlLabel
          sx={{
            m: 0,
            flexBasis: { xs: "100%", sm: "auto" },
          }}
          control={
            <Switch
              size="small"
              checked={
                settings.display.switchToActivePositionsAfterStart
              }
              onChange={(event) =>
                dispatch(
                  updateSettings({
                    ...settings,
                    display: {
                      ...settings.display,
                      switchToActivePositionsAfterStart: event.target.checked,
                    },
                  }),
                )
              }
              disabled={executing}
            />
          }
          label={
            <Typography variant="caption" color="text.secondary">
              {t("settings.display.switchToActivePositionsAfterStart")}
            </Typography>
          }
        />
        <Box
          data-testid="post-start-tab-spacer"
          sx={{ display: { xs: "none", sm: "block" }, flexGrow: 1 }}
        />
        <Box
          data-testid="strategy-start-actions"
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <Button onClick={onClose} variant="outlined" disabled={executing}>
            {t("traderDashboard.standbyHedged.cancel")}
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color="primary"
            disabled={
              executing ||
              futuresMarketUnavailable ||
              spotMarketUnavailable ||
              budgetGuardrailBlocksExecution ||
              !jsonPayload ||
              (settings.useMultiTierProfit && !isMultiTierVolumeValid)
            }
            startIcon={
              executing ? <CircularProgress size={20} /> : <RocketLaunchIcon />
            }
            sx={{
              minWidth: 150,
              background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #1565c0 0%, #1976d2 100%)",
              },
            }}
          >
            {executing
              ? t("traderDashboard.standbyHedged.executing")
              : t("traderDashboard.standbyHedged.execute")}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
